<template>
    <div class="stacked-bar-chart" ref="chartContainer"></div>
</template>

<script>
import Vue from "vue";
import * as echarts from "echarts/core";
import { BarChart } from "echarts/charts";
import { 
    GridComponent, 
    TooltipComponent, 
    LegendComponent, 
    TitleComponent,
    MarkLineComponent 
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import _ from 'lodash';

echarts.use([
    BarChart, 
    GridComponent, 
    TooltipComponent, 
    LegendComponent, 
    TitleComponent, 
    MarkLineComponent,
    CanvasRenderer
]);

export default Vue.extend({
    name: "StackedBarChart",
    props: {
        data: {
            type: Array,
            required: true
        },
        settings: {
            type: Object,
            required: true
        },
        title: {
            type: String,
            default: ""
        },
        fields: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            chart: null,
            resizeObserver: null
        };
    },
    computed: {
        chartOptions() {
            // X ekseni field'ını bul
            const xAxisField = this.fields.find(f => f._id === _.get(this.settings, 'xAxis.field'));
            if (!xAxisField) return null;

            // Stack by field'ını bul
            const stackByField = this.fields.find(f => f._id === _.get(this.settings, 'stackBy.field'));
            if (!stackByField) return null;

            // Y ekseni field'ını bul (eğer calculationType count değilse)
            const yAxisField = _.get(this.settings, 'yAxis.calculationType') !== 'count' 
                ? this.fields.find(f => f._id === _.get(this.settings, 'yAxis.field'))
                : null;

            // Veriyi sırala
            let sortedData = [...this.data];
            if (_.get(this.settings, 'customization.sortBy')) {
                const [axis, direction] = this.settings.customization.sortBy.split('-');
                const sortOrder = direction === 'asc' ? 1 : -1;
                
                sortedData.sort((a, b) => {
                    if (axis === 'x') {
                        return sortOrder * (a[xAxisField.name] > b[xAxisField.name] ? 1 : -1);
                    } else {
                        const aValue = Object.values(a).reduce((sum, val) => 
                            typeof val === 'number' ? sum + val : sum, 0);
                        const bValue = Object.values(b).reduce((sum, val) => 
                            typeof val === 'number' ? sum + val : sum, 0);
                        return sortOrder * (aValue - bValue);
                    }
                });
            }

            // Top/Bottom filtreleme
            if (_.get(this.settings, 'customization.showOnlyEnabled') && 
                _.get(this.settings, 'customization.showOnly')) {
                const { mode, take } = this.settings.customization.showOnly;
                if (mode === 'top') {
                    sortedData = sortedData.slice(0, take);
                } else if (mode === 'bottom') {
                    sortedData = sortedData.slice(-take);
                }
            }

            // Stack gruplarını oluştur
            const stackGroups = new Set();
            sortedData.forEach(item => {
                Object.keys(item).forEach(key => {
                    if (key !== xAxisField.name) {
                        stackGroups.add(key);
                    }
                });
            });

            // X ekseni verilerini hazırla
            const xAxisData = sortedData.map(item => {
                const value = item[xAxisField.name];
                const customization = _.get(this.settings, `customization.labelCustomization.${value}`);
                return customization?.label || value;
            });

            // Stack modu ve yerleşimi
            const stackMode = _.get(this.settings, 'stackBy.stackMode', 'stacked'); // 'stacked' veya 'grouped'
            const expandMode = this.settings.stackBy?.expandMode || 'vertical';

            // Serileri hazırla
            const series = Array.from(stackGroups).map(group => {
                const customization = _.get(this.settings, `customization.labelCustomization.${group}`);
                return {
                    name: customization?.label || group,
                    type: 'bar',
                    stack: stackMode === 'stacked' ? 'total' : group,
                    emphasis: {
                        focus: 'series'
                    },
                    itemStyle: {
                        color: customization?.color
                    },
                    data: sortedData.map(item => item[group] || 0)
                };
            });

            // Y ekseni etiketi
            const yAxisLabel = yAxisField 
                ? `${yAxisField.name} (${_.get(this.settings, 'yAxis.calculationType')})`
                : `Kayıt Sayısı`;

            // Benchmark çizgilerini hazırla
            const benchmarkLines = this.settings.benchmarkLines || [];
            const markLineData = benchmarkLines.map((line) => ({
                name: line.label,
                yAxis: line.axis === 'y' ? line.value : null,
                xAxis: line.axis === 'x' ? line.value : null,
                lineStyle: {
                    color: line.color,
                },
                label: {
                    show: true,
                    position: 'end',
                    formatter: () => `${line.label}: ${line.value}`,
                },
            }));

            // Grafik ayarları
            const isHorizontal = expandMode === 'horizontal';
            const options = {
                title: {
                    text: this.title,
                    left: 'center'
                },
                tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                        type: 'shadow'
                    }
                },
                legend: {
                    top: '30px',
                    type: 'scroll'
                },
                grid: {
                    left: '3%',
                    right: '4%',
                    bottom: '3%',
                    top: '80px',
                    containLabel: true
                },
                series: series.map(s => ({
                    ...s,
                    ...(isHorizontal ? { encode: { x: 'value', y: 'name' } } : {}),
                    markLine: {
                        silent: true,
                        symbol: ['none', 'none'],
                        data: markLineData
                    }
                }))
            };

            // Yatay/Dikey mod ayarları
            if (isHorizontal) {
                options.xAxis = {
                    type: 'value',
                    name: yAxisLabel
                };
                options.yAxis = {
                    type: 'category',
                    data: xAxisData,
                    name: xAxisField.name,
                    axisLabel: {
                        interval: 0
                    }
                };
            } else {
                options.xAxis = {
                    type: expandMode === 'vertical' ? 'category' : 'value',
                    data: expandMode === 'vertical' ? xAxisData : null,
                };
                options.yAxis = {
                    type: expandMode === 'vertical' ? 'value' : 'category',
                    data: expandMode === 'vertical' ? null : xAxisData,
                    name: yAxisLabel
                };
            }

            return options;
        }
    },
    mounted() {
        this.initChart();
        this.initResizeObserver();
    },
    beforeDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        if (this.chart) {
            this.chart.dispose();
            this.chart = null;
        }
    },
    methods: {
        getBenchmarkLines() {
            const benchmarkLines = _.get(this.settings, 'benchmarkLines', []);
            if (!benchmarkLines || !benchmarkLines.length) return [];

            return benchmarkLines.map(line => ({
                name: line.label,
                lineStyle: {
                    color: line.color,
                    type: 'dashed',
                    width: 2
                },
                label: {
                    formatter: `${line.label}${line.text ? ': ' + line.text : ''}`,
                    position: 'middle',
                    show: true,
                    color: line.color,
                    fontSize: 12,
                    backgroundColor: '#fff',
                    padding: [4, 8]
                },
                [line.axis === 'x' ? 'xAxis' : 'yAxis']: line.value
            }));
        },
        initChart() {
            if (!this.$refs.chartContainer) return;
            
            this.chart = echarts.init(this.$refs.chartContainer);
            this.updateChart();
        },
        initResizeObserver() {
            this.resizeObserver = new ResizeObserver(_.debounce(() => {
                if (this.chart) {
                    this.chart.resize();
                }
            }, 100));

            if (this.$refs.chartContainer) {
                this.resizeObserver.observe(this.$refs.chartContainer);
            }
        },
        updateChart() {
            if (!this.chart || !this.chartOptions) return;
            this.chart.setOption(this.chartOptions, true);
            this.chart.resize();
        }
    },
    watch: {
        data: {
            handler() {
                this.$nextTick(() => {
                    this.updateChart();
                });
            },
            deep: true
        },
        settings: {
            handler() {
                this.$nextTick(() => {
                    this.updateChart();
                });
            },
            deep: true
        }
    }
});
</script>

<style scoped lang="scss">
.stacked-bar-chart {
    width: 100%;
    height: 100%;
    min-height: 200px;
}
</style> 